import { useCallback, useEffect, useRef, useState } from 'react';
import { FolderOpen, Loader2, X } from 'lucide-react';
import clsx from 'clsx';
import { serverInfoApi, type ServerInfo } from '../lib/api';
import { useAgentConfig, useAgentConfigStore } from '@/lib/agentConfig';

/**
 * Topbar widget that displays the active devices root and lets the user
 * change it.
 *
 * 阶段 A：写路径走 C 类 `PUT /api/agent/config { devices: { dir } }`。
 * 服务端会原子写 `agent-local.json`、调用 `DevicesDir.setRoot()` 让运行
 * 中 manager 立刻用上新目录。
 *
 * Why not a `<input type="file" webkitdirectory>` picker? Browsers only
 * surface a directory handle, NOT an absolute filesystem path string — and
 * the server needs the path string to write artifacts. So we ask the user
 * to type/paste a path and let the backend validate + create it.
 */
export function DeviceFilesDirSetting(): JSX.Element {
  const [info, setInfo] = useState<ServerInfo | null>(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const next = await serverInfoApi.get();
      setInfo(next);
    } catch {
      // Server not yet up / token wrong — keep the badge muted; the rest
      // of the UI surfaces fetch errors more loudly.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const display = info?.devicesRoot ?? '加载中…';
  const friendly = humanisePath(display, info?.homeDir);

  return (
    <>
      <button
        className="hidden items-center gap-1.5 rounded-md border border-line bg-bg-card px-2 py-1 text-xs text-ink-muted hover:text-ink lg:inline-flex"
        title={`点击修改设备文件存放目录\n当前: ${display}`}
        onClick={() => setOpen(true)}
      >
        <FolderOpen className="h-3.5 w-3.5" />
        <span className="font-mono max-w-[280px] truncate">{friendly}</span>
      </button>
      {open && info && (
        <DeviceFilesDirModal
          info={info}
          onClose={() => setOpen(false)}
          onSaved={(next) => {
            setInfo((prev) => (prev ? { ...prev, devicesRoot: next.devicesRoot } : prev));
            setOpen(false);
            // Other panels (Files, FilesTable footer) listen on this event so
            // they can re-fetch without reaching for a global state library.
            window.dispatchEvent(new CustomEvent('adh:devices-dir-changed', { detail: next }));
          }}
        />
      )}
    </>
  );
}

function DeviceFilesDirModal({
  info,
  onClose,
  onSaved,
}: {
  info: ServerInfo;
  onClose: () => void;
  onSaved: (next: { devicesRoot: string; persisted: boolean; persistError?: string }) => void;
}): JSX.Element {
  const agentDir = useAgentConfig((c) => c.devices.dir);
  const [value, setValue] = useState(() =>
    humanisePath(agentDir ?? info.devicesRoot, info.homeDir),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const submit = useCallback(
    async (override?: string | null) => {
      setBusy(true);
      setErr(null);
      setWarn(null);
      try {
        const raw = override === undefined ? value.trim() : override;
        const next = raw === '' || raw === null ? null : raw;
        const store = useAgentConfigStore.getState();
        store.update({ devices: { dir: next } });
        await store.flush();
        // Re-fetch the freshly persisted snapshot for source of truth fields
        // (configFile / homeDir / devicesRoot resolved by server).
        const after = await serverInfoApi.get();
        const persisted = !store.lastError;
        if (!persisted && store.lastError) {
          setWarn(`已切换运行目录，但 agent-local.json 写入失败: ${store.lastError}`);
        }
        onSaved({
          devicesRoot: after.devicesRoot,
          persisted,
          ...(store.lastError ? { persistError: store.lastError } : {}),
        });
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [value, onSaved],
  );

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="card w-full max-w-xl p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">设备文件存放目录（这台电脑）</h2>
          <button className="btn-ghost" onClick={onClose} aria-label="close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-ink-muted">
            从手机拉取的 APK、截图、录屏、日志都会写入此目录。修改会立即生效，并保存到 <code className="font-mono">agent-local.json</code>。
          </p>
          <label className="block text-xs text-ink-muted">
            目录路径（支持 <code className="font-mono">~/...</code>，必须为绝对路径）
          </label>
          <input
            ref={inputRef}
            className={clsx(
              'w-full rounded-md border border-line bg-bg-card px-2 py-1.5 font-mono text-xs',
              'focus:border-accent focus:outline-none',
            )}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void submit();
              } else if (e.key === 'Escape') {
                onClose();
              }
            }}
            placeholder={info.defaultDevicesRoot}
            spellCheck={false}
          />
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-ink-muted">
            <span>快捷:</span>
            <button
              className="btn"
              onClick={() => setValue(humanisePath(info.defaultDevicesRoot, info.homeDir))}
              disabled={busy}
              type="button"
            >
              恢复默认
            </button>
            <span className="font-mono opacity-70">{info.defaultDevicesRoot}</span>
          </div>
          <div className="rounded border border-line bg-bg-soft px-2 py-1.5 text-[11px] text-ink-muted">
            <div>
              当前生效: <span className="font-mono text-ink">{info.devicesRoot}</span>
            </div>
            <div>
              配置文件:{' '}
              <span className="font-mono">
                {info.configFile ?? <em className="opacity-70">未发现 agent-local.json</em>}
              </span>
            </div>
          </div>
          {err && <p className="text-xs text-rose-500">{err}</p>}
          {warn && <p className="text-xs text-amber-500">{warn}</p>}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn" onClick={onClose} disabled={busy} type="button">
            取消
          </button>
          <button
            className="btn-primary"
            onClick={() => void submit()}
            disabled={busy || value.trim().length === 0}
            type="button"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            保存并立即生效
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Replace the leading `<homeDir>` segment with `~` for display. Pure cosmetic;
 * the actual storage is always the absolute path returned by the server.
 */
function humanisePath(p: string, homeDir?: string): string {
  if (!homeDir || homeDir.length === 0) return p;
  if (p === homeDir) return '~';
  if (p.startsWith(homeDir + '/') || p.startsWith(homeDir + '\\')) {
    return '~' + p.slice(homeDir.length);
  }
  return p;
}
